import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './components/HomePage';
import ReceiptPage from './components/ReceiptPage';
import QRPaymentFlow from './components/QRPaymentFlow';
import NFCPaymentFlow from './components/NFCPaymentFlow';
import EmailInputPage from './components/EmailInputPage';
import KioskRegistration from './components/KioskRegistration';
import { isKioskRegistered } from './utils/kioskId';
import { kioskAuthService } from './services/api';
import './App.css';

// Get basename from environment variable (e.g., /kiosk)
// Defaults to empty string for local development
const basename = process.env.REACT_APP_BASENAME || '';

function App() {
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    validateRegistration();
  }, []);

  const validateRegistration = async () => {
    setIsValidating(true);

    // Check if kiosk has a UUID stored
    const registered = isKioskRegistered();

    if (!registered) {
      setIsRegistered(false);
      setIsValidating(false);
      return;
    }

    // Validate the UUID with the backend
    try {
      const kioskId = localStorage.getItem('kiosk_uuid');
      if (kioskId) {
        const result = await kioskAuthService.validate(kioskId);
        setIsRegistered(result.valid);
      } else {
        setIsRegistered(false);
      }
    } catch (error) {
      console.error('Validation failed:', error);
      // If validation fails, show registration screen
      setIsRegistered(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRegistrationComplete = () => {
    setIsRegistered(true);
  };

  // Show loading screen while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Verifying kiosk registration...</p>
        </div>
      </div>
    );
  }

  // Show registration screen if not registered
  if (isRegistered === false) {
    return (
      <ErrorBoundary>
        <KioskRegistration onRegistrationComplete={handleRegistrationComplete} />
      </ErrorBoundary>
    );
  }

  // Show main app if registered and validated
  return (
    <ErrorBoundary>
      <Router basename={basename}>
        <div className="min-h-screen">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/email" element={<EmailInputPage />} />
            <Route path="/payment/qr" element={<QRPaymentFlow />} />
            <Route path="/payment/nfc" element={<NFCPaymentFlow />} />
            <Route path="/receipt" element={<ReceiptPage />} />
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;